import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HoursPerDay, Role } from '@cfl/shared';

import { api } from './api';

/**
 * Administrative writes.
 *
 * Every mutation here invalidates broadly rather than patching a cached row: a
 * changed contract moves every working-time figure for that person, a hidden
 * project disappears from the booking pickers, and a new holiday changes expected
 * hours for everybody in its group. Keeping those in step by hand is how they
 * start disagreeing with the server.
 */
function useAdminMutation<TArgs, TResult = unknown>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of ['people', 'catalog', 'working-time', 'timesheet', 'absence']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

/* --------------------------------------------------------------- employees -- */

export interface EmployeeDraft {
  email: string;
  displayName: string;
  alias?: string | null;
  jobTitle?: string | null;
  accountNumber?: string | null;
  role: Role;
  timezone?: string;
  supervisorId?: string | null;
  /** Major units as typed - "1500" or "1500.50". An empty string clears it. */
  hourlyRate?: string | null;
  internalRate?: string | null;
}

export const useCreateEmployee = () =>
  useAdminMutation((draft: EmployeeDraft) => api.post('/people', draft));

export const useUpdateEmployee = () =>
  useAdminMutation(({ id, ...patch }: Partial<EmployeeDraft> & { id: string }) =>
    api.patch(`/people/${id}`, patch),
  );

export const useSetEmployeeEnabled = () =>
  useAdminMutation(({ id, enabled }: { id: string; enabled: boolean }) =>
    api.post(`/people/${id}/enabled`, { enabled }),
  );

/* --------------------------------------------------------------- contracts -- */

export interface Contract {
  hoursPerDay: HoursPerDay;
  vacationDaysPerYear: number;
  holidayGroup: { id: string; name: string } | null;
  workStartDate: string | null;
  workEndDate: string | null;
  absenceCalculationMode: 'COMPENSATE' | 'REDUCE';
}

export interface ContractDraft {
  hoursPerDay: Record<string, number>;
  vacationDaysPerYear: number;
  holidayGroupId?: string | null;
  workStartDate?: string | null;
  workEndDate?: string | null;
  absenceCalculationMode: 'COMPENSATE' | 'REDUCE';
}

export const useContract = (employeeId: string | null) =>
  useQuery({
    queryKey: ['people', 'contract', employeeId],
    queryFn: () => api.get<Contract | null>(`/people/${employeeId}/contract`),
    enabled: employeeId !== null,
  });

export const useSaveContract = () =>
  useAdminMutation(({ id, ...draft }: ContractDraft & { id: string }) =>
    api.put(`/people/${id}/contract`, draft),
  );

/* ------------------------------------------------------------------- teams -- */

export const useCreateTeam = () =>
  useAdminMutation((name: string) => api.post('/people/teams', { name }));

export const useRenameTeam = () =>
  useAdminMutation(({ id, name }: { id: string; name: string }) =>
    api.patch(`/people/teams/${id}`, { name }),
  );

export const useDeleteTeam = () =>
  useAdminMutation((id: string) => api.delete(`/people/teams/${id}`));

export const useSetMembership = () =>
  useAdminMutation(
    ({ teamId, employeeId, member, isLead }: {
      teamId: string;
      employeeId: string;
      member: boolean;
      isLead?: boolean;
    }) => api.post(`/people/teams/${teamId}/members`, { employeeId, member, isLead }),
  );

/* --------------------------------------------------------------- catalogue -- */

export const useCreateCustomer = () =>
  useAdminMutation((draft: { name: string; number?: string | null; country?: string | null; currency?: string }) =>
    api.post('/catalog/customers', draft),
  );

export const useUpdateCustomer = () =>
  useAdminMutation(
    ({ id, ...patch }: {
      id: string;
      name?: string;
      visible?: boolean;
      /** One of the three levels that decide a new entry's billable flag. */
      billable?: boolean;
      number?: string | null;
    }) => api.patch(`/catalog/customers/${id}`, patch),
  );

export const useCreateProject = () =>
  useAdminMutation((draft: { customerId: string; name: string; number?: string | null; comment?: string | null; color?: string | null; teamIds?: string[] }) =>
    api.post('/catalog/projects', draft),
  );

export const useUpdateProject = () =>
  useAdminMutation(
    ({ id, ...patch }: {
      id: string;
      name?: string;
      visible?: boolean;
      /** Whether hours booked here are sold to a client. Drives utilisation. */
      billable?: boolean;
      /** Whether shared activities may be booked here. */
      globalActivities?: boolean;
      customerId?: string;
      comment?: string | null;
      color?: string | null;
      teamIds?: string[];
    }) => api.patch(`/catalog/projects/${id}`, patch),
  );

export const useCreateActivity = () =>
  useAdminMutation((draft: { name: string; projectId?: string | null }) =>
    api.post('/catalog/activities', draft),
  );

export const useUpdateActivity = () =>
  useAdminMutation(
    ({ id, ...patch }: {
      id: string;
      name?: string;
      visible?: boolean;
      /** One of the three levels that decide a new entry's billable flag. */
      billable?: boolean;
    }) => api.patch(`/catalog/activities/${id}`, patch),
  );

/**
 * Deletes are offered, but the API refuses any row that hours are booked against
 * and says to hide it instead. The error is shown verbatim because it names the
 * count, which is the thing that makes the refusal make sense.
 */
export const useDeleteCatalogItem = () =>
  useAdminMutation(({ kind, id }: { kind: 'customers' | 'projects' | 'activities'; id: string }) =>
    api.delete(`/catalog/${kind}/${id}`),
  );

/* ---------------------------------------------------------------- holidays -- */

export interface HolidayGroup {
  id: string;
  name: string;
  _count: { holidays: number; contracts: number };
}

export interface Holiday {
  id: string;
  date: string;
  name: string;
  mandatory: boolean;
  halfDay: boolean;
  group: { id: string; name: string };
}

/* ---------------------------------------------------- contracts, in bulk -- */

export interface ContractRow {
  id: string;
  displayName: string;
  email: string;
  enabled: boolean;
  /** Null means nobody has set one up - which is not the same as a zero-hour week. */
  contract: Contract | null;
}

export const useContracts = (includeDisabled: boolean) =>
  useQuery({
    queryKey: ['people', 'contracts', { includeDisabled }],
    queryFn: () => api.get<ContractRow[]>(`/people/contracts?includeDisabled=${includeDisabled}`),
  });

export interface HolidayGroupAssignment {
  updated: number;
  group: string;
  /** People with no contract at all, so there was nothing to put the group on. */
  skipped: string[];
}

export const useAssignHolidayGroup = () =>
  useAdminMutation((holidayGroupId: string) =>
    api.post<HolidayGroupAssignment>('/people/contracts/holiday-group', { holidayGroupId }),
  );

export const useHolidayGroups = () =>
  useQuery({
    queryKey: ['catalog', 'holiday-groups'],
    queryFn: () => api.get<HolidayGroup[]>('/catalog/holiday-groups'),
  });

export const useHolidays = (groupId: string | undefined, year: number) =>
  useQuery({
    queryKey: ['catalog', 'holidays', groupId ?? 'all', year],
    queryFn: () =>
      api.get<Holiday[]>(`/catalog/holidays?year=${year}${groupId ? `&groupId=${groupId}` : ''}`),
  });

export const useCreateHoliday = () =>
  useAdminMutation((draft: { groupId: string; date: string; name: string; mandatory?: boolean; halfDay?: boolean }) =>
    api.post('/catalog/holidays', draft),
  );

export const useUpdateHoliday = () =>
  useAdminMutation(({ id, ...patch }: { id: string; name?: string; mandatory?: boolean; halfDay?: boolean }) =>
    api.patch(`/catalog/holidays/${id}`, patch),
  );

export const useDeleteHoliday = () =>
  useAdminMutation((id: string) => api.delete(`/catalog/holidays/${id}`));

export interface CopyYearResult {
  created: number;
  skipped: number;
  note: string;
}

export const useCopyHolidayYear = () =>
  useAdminMutation((args: { groupId: string; fromYear: number; toYear: number }) =>
    api.post<CopyYearResult>('/catalog/holidays/copy-year', args),
  );

/* -------------------------------------------------------------------- tags -- */

export const useCreateTag = () =>
  useAdminMutation((draft: { name: string; color?: string | null }) =>
    api.post('/catalog/tags', draft),
  );

export const useUpdateTag = () =>
  useAdminMutation(({ id, ...patch }: { id: string; name?: string; color?: string | null }) =>
    api.patch(`/catalog/tags/${id}`, patch),
  );

/** Reports how many entries lost the label, which a plain "deleted" would not. */
export const useDeleteTag = () =>
  useAdminMutation((id: string) =>
    api.delete<{ ok: boolean; removedFrom: number }>(`/catalog/tags/${id}`),
  );
